import React, { useMemo, useState, ReactNode } from 'react';

import dataURIToBlob from '../utils/dataURIToBlob';
import getPreview from '../utils/getPreview';
import getRotatedVersions from '../utils/getRotatedVersions';
import styles from './imageRotator.scss';

interface Initial {
  rotateImage: () => void;
  rotateImageRight: () => void;
  rotateImageLeft: () => void;
  saveImage: () => void;
}

export const ImageRotationContext = React.createContext<Initial>({} as Initial);

export interface RotatorSave {
  file: Blob;
  height: number;
  imagePreview: string;
  rotation: number;
  width: number;
}

interface ContextProps {
  file: HTMLImageElement;
  onSave: (info: RotatorSave) => void;
  children: (arg0: ReactNode) => ReactNode | ReactNode[];
  imageStyles?: string;
  imageContainerStyles?: string;
}

export const FlexImageRotator: React.FC<ContextProps> = ({
  file,
  onSave,
  children,
  imageStyles,
  imageContainerStyles,
}) => {
  const imageRotations: HTMLImageElement[] = useMemo(() => getRotatedVersions(file), [file]);
  const [rotatedIndex, setRotatedIndex] = useState(0);

  const rotateImage = (): void => setRotatedIndex(rotatedIndex >= 3 ? 0 : rotatedIndex + 1);
  const rotateImageRight = (): void => setRotatedIndex(rotatedIndex >= 3 ? 0 : rotatedIndex + 1);
  const rotateImageLeft = (): void => setRotatedIndex(rotatedIndex <= 0 ? 3 : rotatedIndex - 1);

  const saveImage = (): void => {
    getPreview({
      base64src: imageRotations[rotatedIndex].src,
      callback: imagePreview => {
        let rotation = Math.abs((4 - rotatedIndex) * 90);

        if (rotation === 360) {
          rotation = 0;
        }

        const { height, width, src } = imageRotations[rotatedIndex];
        onSave({
          file: dataURIToBlob(src),
          height,
          width,
          imagePreview,
          rotation,
        });
      },
    });
  };

  const image = (
    <div className={`${styles.imageContainer} ${imageContainerStyles || ''}`}>
      {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
      <img
        className={`${styles.image} ${imageStyles || ''}`}
        src={imageRotations[rotatedIndex].src}
        alt="cover photo"
      />
    </div>
  );

  const context = {
    rotateImage,
    saveImage,
    rotateImageLeft,
    rotateImageRight,
  };

  return <ImageRotationContext.Provider value={context}>{children && children(image)}</ImageRotationContext.Provider>;
};
